import React, { useState } from 'react'
import { useDrag } from 'react-dnd'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import PersonIcon from '@mui/icons-material/Person'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Menu, MenuItem } from '@mui/material'
import Modal from 'components/atoms/Modal'
import { getTemplateOwnerName } from 'features/analytics/utils/templateProvenance'
import {
  duplicateAnalyticsTemplateRoutine,
  removeAnalyticsTemplateRoutine
} from 'features/analytics/ducks/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'

const TemplateItem = ({ template, canDrag = true }) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [removeModalOpen, setRemoveModalOpen] = useState(false)
  const isLocked = !!(template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked)
  const ownerName = getTemplateOwnerName(template)
  const creatorId = template?.creator_id
    ?? template?.creatorId
    ?? template?.creator?.id
    ?? template?.created_by?.id
    ?? template?.createdBy?.id
    ?? template?.user_id
    ?? template?.userId
  const isCreator = creatorId && currentUser?.id
    ? String(creatorId) === String(currentUser.id)
    : false
  const isAdmin = currentUser?.type === 'admin'
  const canDelete = isCreator || isAdmin

  const openMenu = e => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }
  const closeMenu = e => {
    e && e.stopPropagation()
    setMenuAnchor(null)
  }

  const handleDuplicate = e => {
    e.stopPropagation()
    dispatch(duplicateAnalyticsTemplateRoutine({ id: template.id }))
    setMenuAnchor(null)
  }

  const handleRemoveClick = e => {
    e.stopPropagation()
    setMenuAnchor(null)
    setRemoveModalOpen(true)
  }

  const handleRemoveConfirm = () => {
    dispatch(removeAnalyticsTemplateRoutine({ id: template.id }))
    setRemoveModalOpen(false)
  }
  const [{ opacity }, dragRef] = useDrag(
    () => ({
      type: 'template',
      item: template,
      canDrag,
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1
      })
    }),
    [template, canDrag]
  )

  return (
    <TemplateItemWrapper ref={dragRef} style={{ opacity }} $isLocked={isLocked} $canDrag={canDrag}>
      <div>
        <TemplateName>{template.name}</TemplateName>
        <TemplateMetaRow>
          <StatusBadge $isLocked={isLocked}>
            {isLocked ? <EditOffIcon fontSize="inherit" /> : <EditIcon fontSize="inherit" />}
            {isLocked ? 'Non-editable' : 'Editable'}
          </StatusBadge>
          {ownerName && (
            <OwnerText title={`Owner ${ownerName}`}>
              <PersonIcon fontSize="inherit" />
              <span>Owner</span>
              <OwnerName>{ownerName}</OwnerName>
            </OwnerText>
          )}
        </TemplateMetaRow>
      </div>
      <MenuButton onClick={openMenu} title='Template actions'>
        <MoreVertIcon fontSize='inherit' />
      </MenuButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        onClick={e => e.stopPropagation()}
      >
        <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
        {canDelete && <MenuItem onClick={handleRemoveClick}>Delete</MenuItem>}
      </Menu>
      <Modal
        open={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        title='Delete template'
        withCancel
        withSubmit
        onSubmit={handleRemoveConfirm}
        onCancel={() => setRemoveModalOpen(false)}
        submitButtonText='Delete'
      >
        Do you really want to delete the template <strong>{template.name}</strong>?
      </Modal>
      <DragIconWrapper $isLocked={isLocked}>
        {!canDrag || isLocked ? <EditOffIcon /> : <DragIndicatorIcon />}
      </DragIconWrapper>
    </TemplateItemWrapper>
  )
}

export default TemplateItem

const MenuButton = styled.button`
  position: absolute;
  top: 4px;
  right: 44px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 18px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.grey[500]};
  }
`

const TemplateItemWrapper = styled.div`
  padding: 7px 70px 7px 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  cursor: ${({ $canDrag }) => ($canDrag ? 'grab' : 'not-allowed')};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  opacity: 1;
`

const TemplateName = styled.div`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.2;
  margin-bottom: 4px;
`

const TemplateMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  background: ${({ $isLocked }) => ($isLocked ? 'rgba(180,63,45,0.12)' : 'rgba(79,90,106,0.12)')};
  color: ${({ $isLocked }) => ($isLocked ? '#b43f2d' : '#4f5a6a')};
  white-space: nowrap;
  flex-shrink: 0;
`

const OwnerText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  min-width: 0;
  flex: 1;
`

const OwnerName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DragIconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, $isLocked }) => $isLocked ? theme.colors.grey[500] : theme.colors.grey[400]};
  color: ${({ theme, $isLocked }) => $isLocked ? theme.colors.grey[700] : theme.colors.grey[600]};
  border-left: 1px solid ${({ theme }) => theme.colors.grey[500]};
`
